import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Card,
  CardContent,
  Container,
  Grid,
  Typography,
  Button,
  TextField,
  MenuItem,
  Breadcrumbs,
  Chip,
  Backdrop,
  CircularProgress,
  Tooltip,
  IconButton,
  createMuiTheme
} from '@material-ui/core';
import { makeStyles, ThemeProvider } from '@material-ui/styles';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableContainer from '@material-ui/core/TableContainer';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';
import TablePagination from '@material-ui/core/TablePagination';
import Skeleton from '@material-ui/lab/Skeleton';
import Alert from '@material-ui/lab/Alert';
import SearchIcon from '@material-ui/icons/Search';
import OpenInNewIcon from '@material-ui/icons/OpenInNew';
import LinkIcon from '@material-ui/icons/Link';
import {
  KeyboardDatePicker,
  MuiPickersUtilsProvider
} from '@material-ui/pickers';
import DateFnsUtils from '@date-io/date-fns';
import moment from 'moment';
import { useDispatch, useSelector } from 'react-redux';
import Page from 'src/components/Page';
import { getUser } from 'src/utils/Common';
import { GetRegionByEmployee, GetSubServiceShops, generalActions } from 'src/store/slices/generalSlice';
import { GetOutboundCallActivitiesList, UpdateOutboundCall } from 'src/store/slices/communicationSlice';


// ─── Styles ───────────────────────────────────────────────────────────────────
const useStyles = makeStyles(theme => ({
  root: {
    paddingTop: theme.spacing(3),
    paddingBottom: theme.spacing(3)
  },
  backdrop: {
    zIndex: 1500,
    color: '#fff'
  },
  filterCard: {
    borderRadius: 16,
    boxShadow: '0 2px 12px -4px rgba(0,0,0,.12)',
    marginBottom: theme.spacing(2)
  },
  tableCard: {
    borderRadius: 16,
    boxShadow: '0 2px 12px -4px rgba(0,0,0,.12)'
  },
  searchBtn: {
    height: 40,
    minWidth: 120,
    backgroundColor: '#1E5128',
    color: '#fff',
    '&:hover': {
      backgroundColor: '#155120'
    },
    '&:disabled': {
      backgroundColor: '#9e9e9e'
    }
  },
  tableHead: {
    backgroundColor: '#f4f6f8'
  },
  headCell: {
    fontWeight: 700,
    fontSize: 12,
    letterSpacing: '.5px',
    textTransform: 'uppercase',
    color: '#546e7a',
    whiteSpace: 'nowrap'
  },
  bodyRow: {
    '&:hover': {
      backgroundColor: '#f9fbff'
    }
  },
  chipSurvey: {
    fontSize: 11,
    height: 22,
    fontWeight: 600
  },
  npLink: {
    color: '#1976d2',
    textDecoration: 'none',
    display: 'inline-flex',
    alignItems: 'center',
    gap: 4,
    fontSize: 13,
    '&:hover': { textDecoration: 'underline' }
  },
  pageTitle: {
    fontWeight: 700,
    color: '#263238'
  },
  resultCount: {
    fontSize: 13,
    color: '#78909c',
    fontWeight: 500
  }
}));

const materialTheme = createMuiTheme({
  palette: {
    primary: {
      contrastText: '#FFFFFF',
      dark: '#424242',
      main: '#424242',
      light: '#424242'
    }
  }
});

// ─── Survey status chip colours ───────────────────────────────────────────────
const surveyChipColor = status => {
  switch (status) {
    case 'Open':         return { backgroundColor: '#e8f5e9', color: '#2e7d32' };
    case 'Closed':       return { backgroundColor: '#fdecea', color: '#c62828' };
    case 'Completed':    return { backgroundColor: '#e8f5e9', color: '#2e7d32' };
    case 'Pending':      return { backgroundColor: '#fff3e0', color: '#e65100' };
    case 'Not Sent':     return { backgroundColor: '#fce4ec', color: '#b71c1c' };
    case 'Expired':      return { backgroundColor: '#eeeeee', color: '#616161' };
    default:             return {};
  }
};

// Empty string represents the "All" option for the Survey Status filter
const SURVEY_STATUS_ALL = '';

// ─── Component ────────────────────────────────────────────────────────────────
const OutboundCallActivitiesList = () => {
  const classes = useStyles();
  const dispatch = useDispatch();

  const regionList = useSelector(state => state.general?.regionList ?? []);
  const regionLoading = useSelector(state => state.general?.regionLoading ?? false);
  const subServiceShopList = useSelector(state => state.general?.subServiceShopList ?? []);
  const subServiceShopLoading = useSelector(state => state.general?.subServiceShopLoading ?? false);
  const outboundCallActivitiesResult = useSelector(state => state.communication?.outboundCallActivitiesResult ?? null);
  const outboundCallActivitiesLoading = useSelector(state => state.communication?.outboundCallActivitiesLoading ?? false);
  const outboundCallActivitiesError = useSelector(state => state.communication?.outboundCallActivitiesError ?? null);

  // Filter state
  const [selectedRegion, setSelectedRegion] = useState('');
  const [selectedSubServiceShop, setSelectedSubServiceShop] = useState('');
  const [selectedSurveyStatus, setSelectedSurveyStatus] = useState(SURVEY_STATUS_ALL);
  const [searchTerm, setSearchTerm] = useState('');
  const [startDate, setStartDate] = useState(
    moment().subtract(30, 'days').toDate()
  );
  const [endDate, setEndDate] = useState(new Date());

  // Load regions on mount
  useEffect(() => {
    const user = getUser();
    if (user?.UserId) {
      dispatch(GetRegionByEmployee(user.TechnicianId));
    }
    return () => {
      dispatch(generalActions.clearSubServiceShopList());
    };
  }, [dispatch]);

  const handleRegionChange = (e) => {
    const regionId = e.target.value;
    setSelectedRegion(regionId);
    setSelectedSubServiceShop('');
    dispatch(generalActions.clearSubServiceShopList());
    if (regionId) {
      dispatch(GetSubServiceShops(regionId));
    }
  };

  // Pagination & table state
  const [hasSearched, setHasSearched] = useState(false);
  const [pageNumber, setPageNumber] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [updatingWorkOrderId, setUpdatingWorkOrderId] = useState(null);

  const dispatchFetch = (pn, ps, surveyStatusOverride) => {
    dispatch(
      GetOutboundCallActivitiesList({
        regionId: selectedRegion || 0,
        serviceShopId: selectedSubServiceShop || 0,
        surveyStatus: surveyStatusOverride !== undefined ? surveyStatusOverride : selectedSurveyStatus,
        startDate: moment(startDate).format('YYYY-MM-DD'),
        endDate: moment(endDate).format('YYYY-MM-DD'),
        pageNumber: pn,
        pageSize: ps
      })
    );
  };

  const handleSearch = () => {
    setPageNumber(1);
    dispatchFetch(1, pageSize);
    setHasSearched(true);
  };

  // Survey Status is locked to "All" until a search has been run; changing it re-fetches immediately
  const handleSurveyStatusChange = (e) => {
    const value = e.target.value;
    setSelectedSurveyStatus(value);
    setPageNumber(1);
    dispatchFetch(1, pageSize, value);
  };

  const handleUpdateStatus = async (workOrderId, statusId) => {
    setUpdatingWorkOrderId(workOrderId);
    try {
      await dispatch(UpdateOutboundCall({ WorkOrderId: workOrderId, OutboundCallStatusId: statusId })).unwrap();
      dispatchFetch(pageNumber, pageSize);
    } finally {
      setUpdatingWorkOrderId(null);
    }
  };

  // MUI TablePagination is 0-based; API is 1-based
  const handleChangePage = (_, newPage) => {
    const pn = newPage + 1;
    setPageNumber(pn);
    dispatchFetch(pn, pageSize);
  };

  const handleChangeRowsPerPage = e => {
    const ps = parseInt(e.target.value, 10);
    setPageSize(ps);
    setPageNumber(1);
    dispatchFetch(1, ps);
  };

  const rows = outboundCallActivitiesResult?.Items ?? [];
  // Backend filtering has proven unreliable, so enforce the selected Survey Status on the client too
  const statusFilteredRows = selectedSurveyStatus
    ? rows.filter(row => (row.SurveyStatus || '').trim().toLowerCase() === selectedSurveyStatus.trim().toLowerCase())
    : rows;

  // Free-text search across the visible columns, done entirely on the client
  const filteredRows = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    if (!term) return statusFilteredRows;
    return statusFilteredRows.filter(row => {
      const closeDateStr = row.CloseDate ? moment(row.CloseDate).format('YYYY-MM-DD') : '';
      return [
        row.WorkOrderId,
        closeDateStr,
        row.SurveyStatus,
        row.ProductHiearchy,
        row.WarrantyTypeName,
        row.CustomerName,
        row.CustomerMobilePhone,
        row.WorkOrderTotalPrice,
        row.OutboundCallStatusName
      ].some(val =>
        val !== undefined &&
        val !== null &&
        String(val).toLowerCase().includes(term)
      );
    });
  }, [statusFilteredRows, searchTerm]);

  const columns = [
    { id: 'WorkOrderId',          label: 'Work Order ID' },
    { id: 'CloseDate',            label: 'Closed Date' },
    { id: 'SurveyStatus',         label: 'Survey Status' },
    { id: 'ProductHiearchy',      label: 'Product Hierarchy' },
    { id: 'WarrantyTypeName',     label: 'Warranty Type' },
    { id: 'CustomerName',         label: 'Customer Name' },
    { id: 'CustomerMobilePhone',  label: 'Customer Mobile Nr' },
    { id: 'WorkOrderTotalPrice',  label: 'Work Order Total Price' },
    { id: 'NpsLink',              label: 'NPS Link' },
    { id: 'OutboundCallStatus',   label: 'Outbound Call Status' }
  ];

  return (
    <Page className={classes.root} title="Outbound Call Activities List">
      <Container maxWidth={false}>
        {/* Breadcrumb */}
        <Box mb={2}>
          <Breadcrumbs>
            <Typography color="textSecondary" style={{ fontSize: 13 }}>
              Home
            </Typography>
            <Typography color="textPrimary" style={{ fontSize: 13, fontWeight: 600 }}>
              Outbound Call Activities List
            </Typography>
          </Breadcrumbs>
          <Typography variant="h5" className={classes.pageTitle} style={{ marginTop: 4 }}>
            Outbound Call Activities List
          </Typography>
        </Box>

        {/* Filter Card */}
        <Card className={classes.filterCard}>
          <CardContent>
            <ThemeProvider theme={materialTheme}>
              <MuiPickersUtilsProvider utils={DateFnsUtils}>
                <Grid container spacing={2} alignItems="flex-end">
                  {/* Region */}
                  <Grid item xs={12} sm={6} md={2}>
                    <TextField
                      select
                      fullWidth
                      size="small"
                      label="Region"
                      variant="outlined"
                      value={selectedRegion}
                      onChange={handleRegionChange}
                      disabled={regionLoading}
                    >
                      {regionList.map(r => (
                        <MenuItem key={r.RegionId} value={r.RegionId}>
                          {r.Dsc}
                        </MenuItem>
                      ))}
                    </TextField>
                  </Grid>

                  {/* Sub Service Shop */}
                  <Grid item xs={12} sm={6} md={2}>
                    <TextField
                      select
                      fullWidth
                      size="small"
                      label="Service Shop"
                      variant="outlined"
                      value={selectedSubServiceShop}
                      onChange={e => setSelectedSubServiceShop(e.target.value)}
                      disabled={!selectedRegion || subServiceShopLoading}
                    >
                      <MenuItem value="">
                        {subServiceShopLoading ? 'Loading...' : 'All Service Shops'}
                      </MenuItem>
                      {subServiceShopList.map(s => (
                        <MenuItem key={s.ServiceShopId} value={s.ServiceShopId}>
                          {s.Description}
                        </MenuItem>
                      ))}
                    </TextField>
                  </Grid>

                  {/* Survey Status */}
                  <Grid item xs={12} sm={6} md={2}>
                    <TextField
                      select
                      fullWidth
                      size="small"
                      label="Survey Status"
                      variant="outlined"
                      value={selectedSurveyStatus}
                      onChange={handleSurveyStatusChange}
                      disabled={!hasSearched}
                    >
                      <MenuItem value={SURVEY_STATUS_ALL}>All</MenuItem>
                      <MenuItem value="Open">Open</MenuItem>
                      <MenuItem value="Closed">Closed</MenuItem>
                    </TextField>
                  </Grid>

                  {/* Date From */}
                  <Grid item xs={12} sm={6} md={2}>
                    <KeyboardDatePicker
                      fullWidth
                      inputVariant="outlined"
                      size="small"
                      label="Date From"
                      format="yyyy-MM-dd"
                      value={startDate}
                      onChange={date => setStartDate(date)}
                      KeyboardButtonProps={{ 'aria-label': 'change date from' }}
                    />
                  </Grid>

                  {/* Date To */}
                  <Grid item xs={12} sm={6} md={2}>
                    <KeyboardDatePicker
                      fullWidth
                      inputVariant="outlined"
                      size="small"
                      label="Date To"
                      format="yyyy-MM-dd"
                      value={endDate}
                      onChange={date => setEndDate(date)}
                      KeyboardButtonProps={{ 'aria-label': 'change date to' }}
                    />
                  </Grid>

                  {/* Search Button */}
                  <Grid item xs={12} sm={6} md={2}>
                    <Button
                      fullWidth
                      variant="contained"
                      className={classes.searchBtn}
                      startIcon={<SearchIcon />}
                      onClick={handleSearch}
                      disabled={outboundCallActivitiesLoading}
                    >
                      Search
                    </Button>
                  </Grid>
                </Grid>
              </MuiPickersUtilsProvider>
            </ThemeProvider>
          </CardContent>
        </Card>

        {/* Results Card */}
        <Card className={classes.tableCard}>
          <CardContent style={{ padding: 0 }}>
            {/* Header row */}
            <Box
              display="flex"
              alignItems="center"
              justifyContent="space-between"
              px={2}
              py={1.5}
              borderBottom="1px solid #eceff1"
            >
              <Typography variant="subtitle1" style={{ fontWeight: 700, color: '#37474f' }}>
                Activities
              </Typography>
              <Box display="flex" alignItems="center" style={{ gap: 16 }}>
                <TextField
                  size="small"
                  variant="outlined"
                  placeholder="Search in all columns..."
                  value={searchTerm}
                  onChange={e => setSearchTerm(e.target.value)}
                  disabled={!hasSearched}
                  style={{ minWidth: 260 }}
                  InputProps={{
                    startAdornment: <SearchIcon style={{ marginRight: 8, color: '#78909c', fontSize: 18 }} />
                  }}
                />
                {hasSearched && (
                  <Typography className={classes.resultCount}>
                    {filteredRows.length} record{filteredRows.length !== 1 ? 's' : ''} found
                  </Typography>
                )}
              </Box>
            </Box>

            {/* Error */}
            {outboundCallActivitiesError && (
              <Box px={2} pt={2}>
                <Alert severity="error">
                  {typeof outboundCallActivitiesError === 'string'
                    ? outboundCallActivitiesError
                    : 'An error occurred while loading data. Please try again.'}
                </Alert>
              </Box>
            )}

            {/* Table */}
            <TableContainer className={classes.container} style={{ overflowX: 'auto' }}>
              <Table size="small" style={{ minWidth: 1100 }}>
                <TableHead className={classes.tableHead}>
                  <TableRow>
                    {columns.map(col => (
                      <TableCell key={col.id} className={classes.headCell}>
                        {col.label}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {!hasSearched ? (
                    <TableRow>
                      <TableCell colSpan={columns.length} align="center" style={{ padding: 40 }}>
                        <Typography variant="body2" color="textSecondary">
                          Please select filters and press Search.
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : outboundCallActivitiesLoading ? (
                    Array.from({ length: pageSize > 10 ? 10 : pageSize }).map((_, i) => (
                      <TableRow key={i} className={classes.bodyRow}>
                        {columns.map(col => (
                          <TableCell key={col.id}>
                            <Skeleton variant="text" width="80%" />
                          </TableCell>
                        ))}
                      </TableRow>
                    ))
                  ) : filteredRows.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={columns.length} align="center" style={{ padding: 40 }}>
                        <Typography variant="body2" color="textSecondary">
                          No records found for the selected filters.
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    filteredRows.map((row, idx) => (
                      <TableRow key={row.WorkOrderId} className={classes.bodyRow} style={{ backgroundColor: idx % 2 === 0 ? '#fff' : '#fafbfc' }}>
                        <TableCell style={{ fontWeight: 600, color: '#1565c0', fontSize: 13 }}>
                          {row.WorkOrderId}
                        </TableCell>
                        <TableCell style={{ fontSize: 13, whiteSpace: 'nowrap' }}>
                          {row.CloseDate ? moment(row.CloseDate).format('YYYY-MM-DD') : ''}
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={row.SurveyStatus}
                            size="small"
                            className={classes.chipSurvey}
                            style={surveyChipColor(row.SurveyStatus)}
                          />
                        </TableCell>
                        <TableCell style={{ fontSize: 12, maxWidth: 220, whiteSpace: 'normal' }}>
                          {row.ProductHiearchy}
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={row.WarrantyTypeName}
                            size="small"
                            variant="outlined"
                            style={{ fontSize: 11, height: 22 }}
                          />
                        </TableCell>
                        <TableCell style={{ fontSize: 13, whiteSpace: 'nowrap' }}>
                          {row.CustomerName}
                        </TableCell>
                        <TableCell style={{ fontSize: 13, whiteSpace: 'nowrap' }}>
                          {row.CustomerMobilePhone}
                        </TableCell>
                        <TableCell style={{ fontSize: 13, whiteSpace: 'nowrap' }}>
                          {row.WorkOrderTotalPrice ? row.WorkOrderTotalPrice.toFixed(2) : ''}
                        </TableCell>
                        <TableCell>
                          {row.NpsLink ? (
                            <Tooltip title="Open NP Link">
                              <a
                                href={row.NpsLink}
                                target="_blank"
                                rel="noopener noreferrer"
                                className={classes.npLink}
                              >
                                <LinkIcon style={{ fontSize: 16 }} />
                                Link
                              </a>
                            </Tooltip>
                          ) : null}
                        </TableCell>
                        <TableCell style={{ whiteSpace: 'nowrap' }}>
                          {row.OutboundCallStatusId == null || row.OutboundCallStatusId === 3 ? (
                            <Box display="flex" flexDirection="row" flexWrap="wrap" style={{ gap: 4 }}>
                              {row.OutboundCallStatusId === 3 && (
                                <Chip
                                  label={row.OutboundCallStatusName}
                                  size="small"
                                  style={{ fontSize: 11, height: 22, fontWeight: 600, backgroundColor: '#fff3e0', color: '#e65100', marginBottom: 2 }}
                                />
                              )}
                              <Button
                                size="small"
                                variant="contained"
                                disabled={updatingWorkOrderId === row.WorkOrderId}
                                style={{ fontSize: 11, backgroundColor: '#2e7d32', color: '#fff', textTransform: 'none', lineHeight: 1.3 }}
                                onClick={() => handleUpdateStatus(row.WorkOrderId, 2)}
                              >
                                {updatingWorkOrderId === row.WorkOrderId ? <CircularProgress size={12} color="inherit" style={{ marginRight: 4 }} /> : null}
                                Called and Completed
                              </Button>
                              {row.OutboundCallStatusId == null && (
                                <Button
                                  size="small"
                                  variant="contained"
                                  disabled={updatingWorkOrderId === row.WorkOrderId}
                                  style={{ fontSize: 11, backgroundColor: '#1D3343', color: '#fff', textTransform: 'none', lineHeight: 1.3 }}
                                  onClick={() => handleUpdateStatus(row.WorkOrderId, 3)}
                                >
                                  {updatingWorkOrderId === row.WorkOrderId ? <CircularProgress size={12} color="inherit" style={{ marginRight: 4 }} /> : null}
                                  Called but customer did not respond
                                </Button>
                              )}
                            </Box>
                          ) : (
                            <Chip
                              label={row.OutboundCallStatusName}
                              size="small"
                              style={{
                                fontSize: 11,
                                height: 22,
                                fontWeight: 600,
                                backgroundColor: '#e8f5e9',
                                color: '#2e7d32'
                              }}
                            />
                          )}
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            {hasSearched && (outboundCallActivitiesResult?.TotalCount ?? 0) > 0 && (
              <TablePagination
                rowsPerPageOptions={[10, 20, 50]}
                component="div"
                count={outboundCallActivitiesResult?.TotalCount ?? 0}
                rowsPerPage={pageSize}
                page={pageNumber - 1}
                onChangePage={handleChangePage}
                onChangeRowsPerPage={handleChangeRowsPerPage}
              />
            )}
          </CardContent>
        </Card>
      </Container>

      {/* Backdrop – shown only while loading region/shop dropdowns */}
      <Backdrop className={classes.backdrop} open={regionLoading || subServiceShopLoading}>
        <CircularProgress color="inherit" />
      </Backdrop>
    </Page>
  );
};

export default OutboundCallActivitiesList;
